import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { useAppSelector } from 'hooks/reduxHooks'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useHasChatTripPlannerChats from './useHasChatTripPlannerChats'
import useHasClassicTripPlannerTrips from './useHasClassicTripPlannerTrips'

type ChatTripPlannerDirectEntryDecision = {
  isLoggedIn: boolean
  hasChatTripPlannerDirectEntrySignal: boolean
  hasClassicTripPlannerTrips: boolean
  isLoadingChatTripPlannerDirectEntryDecision: boolean
  hasChatTripPlannerDirectEntryDecisionError: boolean
}

export function shouldUseChatTripPlannerDirectEntryPoint(
  {
    isLoggedIn,
    hasChatTripPlannerDirectEntrySignal,
    hasClassicTripPlannerTrips,
    isLoadingChatTripPlannerDirectEntryDecision,
    hasChatTripPlannerDirectEntryDecisionError,
  }: ChatTripPlannerDirectEntryDecision,
) {
  return (
    isLoggedIn &&
    hasChatTripPlannerDirectEntrySignal &&
    !hasClassicTripPlannerTrips &&
    !isLoadingChatTripPlannerDirectEntryDecision &&
    !hasChatTripPlannerDirectEntryDecisionError
  )
}

function useChatTripPlannerReleaseState(enabled = true) {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isChatTripPlannerReleaseEnabled = !!useOptimizelyExperiment(
    OptimizelyFeatureFlags.chatTripPlannerRelease,
    enabled,
  )
  const shouldLookUpChatTripPlannerChats =
    enabled && isLoggedIn && !isChatTripPlannerReleaseEnabled
  const {
    hasChatTripPlannerChats,
    hasChatTripLookupError,
    isLoadingChatTripPlannerChats,
  } = useHasChatTripPlannerChats(shouldLookUpChatTripPlannerChats)
  const hasChatTripPlannerDirectEntrySignal =
    isChatTripPlannerReleaseEnabled || hasChatTripPlannerChats
  const {
    hasClassicTripPlannerTrips,
    hasClassicTripLookupError,
    isLoadingClassicTripPlannerTrips,
  } = useHasClassicTripPlannerTrips(
    enabled && isLoggedIn && hasChatTripPlannerDirectEntrySignal,
  )
  const isLoadingChatTripPlannerDirectEntryDecision =
    isLoadingChatTripPlannerChats ||
    (hasChatTripPlannerDirectEntrySignal && isLoadingClassicTripPlannerTrips)
  const hasChatTripPlannerDirectEntryDecisionError =
    hasChatTripLookupError ||
    (hasChatTripPlannerDirectEntrySignal && hasClassicTripLookupError)

  return {
    isLoggedIn,
    isChatTripPlannerReleaseEnabled,
    hasChatTripPlannerChats,
    hasChatTripPlannerDirectEntrySignal,
    hasChatTripLookupError,
    isLoadingChatTripPlannerChats,
    hasClassicTripPlannerTrips,
    hasClassicTripLookupError,
    isLoadingClassicTripPlannerTrips,
    isLoadingChatTripPlannerDirectEntryDecision,
    hasChatTripPlannerDirectEntryDecisionError,
    shouldUseChatTripPlannerDirectEntryPoint:
      shouldUseChatTripPlannerDirectEntryPoint(
        {
          isLoggedIn,
          hasChatTripPlannerDirectEntrySignal,
          hasClassicTripPlannerTrips,
          isLoadingChatTripPlannerDirectEntryDecision,
          hasChatTripPlannerDirectEntryDecisionError,
        },
      ),
  }
}

export default useChatTripPlannerReleaseState
